import * as React from 'react';
import classNames from 'classnames';
import Icon from '../../icon';
import RCPicker from 'rc-picker';
import { PickerMode } from 'rc-picker/lib/interface';
import { GenerateConfig } from 'rc-picker/lib/generate/index';
import enUS from '../locale/en_US';
import { getPlaceholder } from '../util';
import devWarning from 'antd/lib/_util/warning';
import { ConfigContext } from 'antd/lib/config-provider/context';

import { ConfigConsumerEProps } from "../../config-provider";
import LocaleReceiver from 'antd/lib/locale-provider/LocaleReceiver';
import PagePoint from "../../page-point";

import {
  PickerProps,
  PickerLocale,
  PickerDateProps,
  PickerTimeProps,
  getTimeProps,
  Components,
} from '.';

const {
    sendPagePoint,
    pagePointConfig: { regKey },
    EventType: { clickType}
  } = PagePoint;

export default function generatePicker<DateType>(generateConfig: GenerateConfig<DateType>) {
  type DatePickerProps = PickerProps<DateType> & {[propName: string]: any};

  function getPicker<InnerPickerProps extends DatePickerProps>(
    picker?: PickerMode,
    displayName?: string,
  ) {
    class Picker extends React.Component<InnerPickerProps> {
      static componentName = 'Picker';
      static contextType = ConfigContext as any;

      static displayName: string;

      context: ConfigConsumerEProps;

      origValue: any;

      pickerRef = React.createRef<RCPicker<DateType>>();

      constructor(props: InnerPickerProps) {
        super(props);
        devWarning(
          picker !== 'quarter',
          displayName!,
          `DatePicker.${displayName} is legacy usage. Please use DatePicker[picker='${picker}'] directly.`,
        );
      }

      focus = () => {
        if (this.pickerRef.current) {
          this.pickerRef.current.focus();
        }
      };

      blur = () => {
        if (this.pickerRef.current) {
          this.pickerRef.current.blur();
        }
      };

      getDefaultLocale = () => {
        const { locale } = this.props;
        const result = {
          ...enUS,
          ...locale,
        };
        result.lang = {
          ...result.lang,
          ...((locale || {}) as PickerLocale).lang,
        };
        return result;
      };

      renderPicker = (locale: PickerLocale) => {
        const { getPrefixCls, getPopupContainer, pagePoint } = this.context;
        const {
          prefixCls: customizePrefixCls,
          getPopupContainer: customizeGetPopupContainer,
          getCalendarContainer,
          monthCellContentRender,
          monthCellRender:customizeMonthCellRender,
          className,
          onChange,
          size: customizeSize,
          bordered = true,
          placeholder,
          showDateInput,
          inputReadOnly,
          [regKey]: dataRegId,
          ...restProps
        } = this.props;
        const { format, showTime } = this.props as any;
        const prefixCls = getPrefixCls('picker', customizePrefixCls);

        const additionalProps = {
          showToday: true,
        };

        let newChange = onChange;
        if (pagePoint && dataRegId) {
            newChange = (dateMomnet, dateString) => {
              onChange && onChange(dateMomnet, dateString);
              let dateStr = dateString;
              // rangepicker为数组
              if (Array.isArray(dateString)) {
                dateStr = JSON.stringify(dateString);
              }
              // change 发送 click
              sendPagePoint({
                eventType: clickType,
                tagname: "datetimePicker",
                value: dateStr,
                [regKey]: dataRegId,
                origValue: this.origValue
              });
              this.origValue = dateStr;
            };
          }

        const inputRO = showDateInput===false ? inputReadOnly || true : inputReadOnly;

        let additionalOverrideProps: any = {};
        if (picker) {
          additionalOverrideProps.picker = picker;
        }
        const mergedPicker = picker || this.props.picker;

        additionalOverrideProps = {
          ...additionalOverrideProps,
          ...(showTime ? getTimeProps({ format, picker: mergedPicker, ...showTime }) : {}),
          ...(mergedPicker === 'time'
            ? getTimeProps({ format, ...this.props, picker: mergedPicker })
            : {}),
        };

        const mergedSize = customizeSize;
              return (
                <RCPicker<DateType>
                  ref={this.pickerRef}
                  placeholder={getPlaceholder(mergedPicker, locale, placeholder)}
                  suffixIcon={
                    mergedPicker === 'time' ? <Icon type="clock-circle" /> : <Icon type="calendar" />
                  }
                  clearIcon={<Icon type="close-circle" />}
                  allowClear
                 //  transitionName="slide-up"
                  {...additionalProps}
                  {...restProps}
                  {...additionalOverrideProps}
                  locale={locale!.lang}
                  className={classNames(className, {
                    [`${prefixCls}-${mergedSize}`]: mergedSize,
                    [`${prefixCls}-borderless`]: !bordered,
                  })}
                  prefixCls={prefixCls}
                  getPopupContainer={customizeGetPopupContainer || getCalendarContainer || getPopupContainer}
                  monthCellRender = {customizeMonthCellRender || monthCellContentRender}
                  generateConfig={generateConfig}
                  prevIcon={<span className={`${prefixCls}-prev-icon`} />}
                  nextIcon={<span className={`${prefixCls}-next-icon`} />}
                  superPrevIcon={<span className={`${prefixCls}-super-prev-icon`} />}
                  superNextIcon={<span className={`${prefixCls}-super-next-icon`} />}
                  components={Components}
                  onChange={newChange}
                  inputReadOnly={inputRO}
                />
              );
      };

      render() {
        return (
          <LocaleReceiver componentName="DatePicker" defaultLocale={this.getDefaultLocale}>
            {this.renderPicker}
          </LocaleReceiver>
        );
      }
    }

    if (displayName) {
      Picker.displayName = displayName;
    }

    return Picker as React.ComponentClass<InnerPickerProps>;
  }

  const DatePicker = getPicker<DatePickerProps>();
  const WeekPicker = getPicker<Omit<PickerDateProps<DateType>, 'picker'>>('week', 'WeekPicker');
  const MonthPicker = getPicker<Omit<PickerDateProps<DateType>, 'picker'>>('month', 'MonthPicker');
  const YearPicker = getPicker<Omit<PickerDateProps<DateType>, 'picker'>>('year', 'YearPicker');
  const TimePicker = getPicker<Omit<PickerTimeProps<DateType>, 'picker'>>('time', 'TimePicker');
  const QuarterPicker = getPicker<Omit<PickerTimeProps<DateType>, 'picker'>>(
    'quarter',
    'QuarterPicker',
  );

  return { DatePicker, WeekPicker, MonthPicker, YearPicker, TimePicker, QuarterPicker };
}



// WEBPACK FOOTER //
// ./src/components/date-picker/generatePicker/generateSinglePicker.tsx